/**
 * Glossary — Konsey Cognitive Renaissance, Bölüm B4.
 *
 * Tüm teknik terimler için merkezi sözlük. `<TermPill term="...">` bu
 * sözlüğü tüketir; UI'daki Pill'ler artık hover'da Türkçe açıklama +
 * (opsiyonel) docs link'i sunar.
 *
 * Yeni terim eklerken:
 *   1. Anahtar (id) lower-snake_case (örn. `sycophancy`).
 *   2. `tr` görüntülenecek Türkçe başlık.
 *   3. `desc` 2 cümleyi geçmesin (tooltip taşar).
 *   4. `more` opsiyonel — projenin docs sayfasına relative link.
 */
export interface GlossaryEntry {
  /** Türkçe başlık (Pill metni / tooltip header). */
  tr: string;
  /** Kısa Türkçe açıklama (≤ 140 char). */
  desc: string;
  /** Opsiyonel "daha fazla" link. */
  more?: string;
}

export const GLOSSARY: Record<string, GlossaryEntry> = {
  // ─── Konsensüs / tartışma akışı ────────────────────────────────────
  consensus: {
    tr: "Konsensüs",
    desc:
      "Konseyin görüşlerinin matematiksel olarak yakınlaşması (cosine ≥ 0.92). " +
      "Genellikle erken finalize tetikleyicisidir.",
    more: "/docs/glossary#consensus",
  },
  deadlock: {
    tr: "Tartışma Çıkmazı",
    desc:
      "Konsey turlar boyunca anlamlı ilerleme kaydedemiyor; skor değişmiyor. " +
      "Genellikle iki ardışık skor farkı < 0.025 (orta profil) eşiğinde tetiklenir.",
    more: "/docs/glossary#deadlock",
  },
  max_rounds: {
    tr: "Tur Bütçesi Tükendi",
    desc:
      "Konsey ne konsensüs ne deadlock'a ulaştı; kullanıcının seçtiği tur " +
      "bütçesi bittiği için kapandı.",
  },
  sycophancy: {
    tr: "Aşırı Uyum",
    desc:
      "Ajanların eleştiri yerine birbirini onaylayarak yapay konsensüse gitmesi. " +
      "Sıkı profilde otomatik Devil's Advocate çağrılır.",
    more: "/docs/glossary#sycophancy",
  },
  devil_advocate: {
    tr: "Devil's Advocate",
    desc:
      "Konsensüs'e karşı argüman üretmek için davet edilen geçici uzman. " +
      "Aşırı uyum tespitinde otomatik tetiklenir.",
  },
  hierarchical_synthesis: {
    tr: "Katmanlı Sentez",
    desc:
      "Büyük tartışmalarda (≥20 tur) tur özetleri + persona pozisyonları + " +
      "(≥60 tur ise) era özetleri ile context patlamasını engelleyen yapı.",
  },

  // ─── Topoloji ──────────────────────────────────────────────────────
  round_robin: {
    tr: "Sıralı Tartışma",
    desc:
      "Her konsey üyesi sırayla konuşur; klasik panel formatı. En yüksek " +
      "öngörülebilirlik, orta hızlı yakınsama.",
  },
  cross_fire: {
    tr: "Çapraz Ateş",
    desc:
      "Her tur farklı uzman ikilisi üzerinden geçer; karşıt görüşler " +
      "doğrudan çarpışır. Hızlı yakınsama veya dramatik deadlock.",
  },
  diverse_poles: {
    tr: "Karşıt Kutuplar",
    desc:
      "Konsey ilk turda iki kutba ayrılır; sonraki turlarda kutuplar " +
      "argüman alışverişi yapar. Stratejik müzakere için ideal.",
  },
  solo_then_debate: {
    tr: "Önce Solo / Sonra Tartışma",
    desc:
      "Round 0 herkes paralel konuşur (kontaminasyon yok); round ≥1 " +
      "tartışma serbest. DOWN paper hipotezi tabanlı.",
  },

  // ─── RAG + araştırma ───────────────────────────────────────────────
  rag: {
    tr: "Belge Destekli Üretim (RAG)",
    desc:
      "Yüklediğiniz belgelerden semantik olarak ilgili pasajların aktif " +
      "konuşmacının prompt'una otomatik eklenmesi.",
    more: "/docs/glossary#rag",
  },

  // ─── Versiyonlama ──────────────────────────────────────────────────
  fork: {
    tr: "Versiyon Türetme",
    desc:
      "Mevcut tartışmanın aynı kadrosunu yeni parametrelerle (tur sayısı, " +
      "rolelar, model) yeniden çalıştırma; ebeveyne bağlı kalır.",
  },
  duplicate: {
    tr: "Bağımsız Kopya",
    desc:
      "Mevcut tartışmanın yeni bir lineage başlatan, ebeveyne bağlı " +
      "olmayan kopyası.",
  },

  // ─── Eylem planı çerçeveleri ───────────────────────────────────────
  raci: {
    tr: "RACI Matrisi",
    desc:
      "Eylem maddesinin Sorumlu (Responsible), Hesap Veren (Accountable), " +
      "Danışılan (Consulted), Bilgilendirilen (Informed) atamaları.",
  },
  moscow: {
    tr: "MoSCoW Önceliklendirme",
    desc:
      "Eylem maddelerinin Must-have / Should-have / Could-have / " +
      "Won't-have olarak sınıflandırılması.",
  },
  smart: {
    tr: "SMART Hedef",
    desc:
      "Specific, Measurable, Achievable, Relevant, Time-bound — başarı " +
      "metriği şeması.",
  },

  // ─── Konsey üyesi modelleri ───────────────────────────────────────
  cognitive_disposition: {
    tr: "Bilişsel Duruş",
    desc:
      "Konsey üyesinin karar verme stili (analitik, sezgisel, riskten " +
      "kaçınan, vb.). Persona yönetiminde takson olarak kullanılır.",
  },
  critique_first: {
    tr: "Önce Kritik",
    desc:
      "Her konuşma öncesi bir önceki konuşmacının argümanını eleştirme " +
      "zorunluluğu. Yapay onay'ı engeller.",
  },

  // ─── AgentBrain (8 lob) ────────────────────────────────────────────
  hippocampus: {
    tr: "Hipokampus",
    desc:
      "Kısa süreli bellek — son ~10 dakikalık RAG / vektör buffer. " +
      "Hızlı geri çağırma için optimize edilmiştir.",
    more: "/docs/brain#hippocampus",
  },
  cortex: {
    tr: "Serebral Korteks",
    desc:
      "Uzun süreli bilgi tabanı — persona uzmanlığı + sistem prompt'u + " +
      "öğrenilen yeni kavramlar.",
    more: "/docs/brain#cortex",
  },
  prefrontal: {
    tr: "Prefrontal Korteks",
    desc:
      "Karar verme + planlama + bilişsel yük takibi. Tool kullanımını " +
      "yönetir, neyin söyleneceğini belirler.",
    more: "/docs/brain#prefrontal",
  },
  amygdala: {
    tr: "Amigdala",
    desc:
      "Duygusal ton + tehdit algısı. Tartışmadaki polariteyi (-1..+1) " +
      "ve sıcaklığı (0..1) ölçer.",
    more: "/docs/brain#amygdala",
  },
  cerebellum: {
    tr: "Serebellum",
    desc:
      "Prosedürel bellek — tekrarlayan görevler (web araması, RAG, " +
      "format dönüşümleri) için özel motor.",
    more: "/docs/brain#cerebellum",
  },
  parietal: {
    tr: "Parietal Lob",
    desc:
      "Odak + dikkat. Konsey üyesinin şu an context'te neye baktığını " +
      "gösterir (focus_target).",
    more: "/docs/brain#parietal",
  },
  temporal: {
    tr: "Temporal Lob",
    desc:
      "Dilsel anlama — gelen metnin semantik bütünleştirme skoru. Yüksek " +
      "skor = doğru anlamış demektir.",
    more: "/docs/brain#temporal",
  },
  occipital: {
    tr: "Oksipital Lob",
    desc:
      "Görsel girdi işleme — RAG'dan gelen görsel kaynaklar (image " +
      "PDF'leri, grafikler).",
    more: "/docs/brain#occipital",
  },
  cognitive_load: {
    tr: "Bilişsel Yük",
    desc:
      "Persona'nın aktif context'ini model context window'una bölüm " +
      "(yüzde olarak). %90+ → token bütçesi tükenmek üzere.",
  },
  learning_coefficient: {
    tr: "Öğrenme Katsayısı",
    desc:
      "(Tartışma sonu bilgi) / (Tartışma başı baseline). Persona'nın bu " +
      "tartışmada ne kadar yeni bilgi içselleştirdiğinin ölçüsü.",
  },
  neural_latency: {
    tr: "Nöral Gecikme",
    desc:
      "Persona'nın input algısı ile output üretimi arasındaki ortalama " +
      "ms gecikme — Cerebellum metric'i.",
  },

  // ─── Simülasyon / kaos ────────────────────────────────────────────
  chaos_simulation: {
    tr: "Kaos Simülasyonu",
    desc:
      "Eylem planının 8 farklı kriz senaryosunda hayatta kalma oranı. " +
      "Düşük skor = kırılgan plan, pivot gerekli.",
  },
  survivability: {
    tr: "Hayatta Kalma Oranı",
    desc:
      "Bir senaryoda eylem planının geçerli kalma yüzdesi (0-100%). " +
      "Birden fazla senaryonun ortalaması.",
  },
};

/**
 * Tip-güvenli anahtar listesi — tooltip'in olası term key'leri için.
 */
export type GlossaryKey = keyof typeof GLOSSARY;

/**
 * Sözlükte yoksa null döner (TermPill silent fallback yapar — sadece Pill).
 */
export function getGlossaryEntry(key: string): GlossaryEntry | null {
  if (!key) return null;
  return GLOSSARY[key.toLowerCase().replace(/-/g, "_")] ?? null;
}
